import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DrawerProvider, useDrawer } from "@/context/DrawerContext";
import GlobalSearch from "@/components/GlobalSearch";
import LeadDrawer from "@/components/LeadDrawer";
import {
  LayoutGrid, UserPlus, Target, BarChart3, FileText, Trophy, Users2, LogOut, Zap, FileSpreadsheet, Search, Sun, Contact, BookText, BookOpen, ClipboardList
} from "lucide-react";

const NAV = [
  { to: "/myday", label: "My Day", icon: Sun, roles: ["admin", "leadgen", "bde"], tid: "nav-myday" },
  { to: "/prospects", label: "Prospecting", icon: Contact, roles: ["admin", "leadgen", "bde"], tid: "nav-prospects" },
  { to: "/prospects-manual", label: "Prospecting Manual", icon: ClipboardList, roles: ["admin"], tid: "nav-prospects-manual" },
  { to: "/", label: "Pipeline", icon: LayoutGrid, roles: ["admin", "leadgen", "bde"], end: true, tid: "nav-pipeline" },
  { to: "/playbook", label: "Playbook", icon: BookOpen, roles: ["admin", "leadgen", "bde"], tid: "nav-playbook" },
  { to: "/intake", label: "Lead Intake", icon: UserPlus, roles: ["admin", "leadgen", "bde"], tid: "nav-intake" },
  { to: "/icp", label: "ICP Engine", icon: Target, roles: ["admin", "leadgen"], tid: "nav-icp" },
  { to: "/won", label: "Won Clients", icon: Trophy, roles: ["admin", "bde"], tid: "nav-won" },
  { to: "/resources", label: "AI Resources", icon: BookText, roles: ["admin"], tid: "nav-resources" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["admin"], tid: "nav-analytics" },
  { to: "/reports", label: "Sales Report", icon: FileSpreadsheet, roles: ["admin"], tid: "nav-reports" },
  { to: "/templates", label: "Templates", icon: FileText, roles: ["admin"], tid: "nav-templates" },
  { to: "/users", label: "Team", icon: Users2, roles: ["admin"], tid: "nav-users" },
];

const ROLE_LABEL = { admin: "Admin", leadgen: "Lead-Gen", bde: "BDE" };

export default function AppShell() {
  return (
    <DrawerProvider>
      <Shell />
    </DrawerProvider>
  );
}

function Shell() {
  const { user, logout } = useAuth();
  const { openId, close, bump } = useDrawer();
  const [searchOpen, setSearchOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex bg-[#F7F7F9]">
      <aside className="w-60 shrink-0 bg-[#0A0A0B] text-white flex flex-col">
        <div className="px-5 py-5">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-klein text-white grid place-items-center shadow-lg shadow-klein/30">
              <Zap className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-head font-black text-sm leading-none tracking-tight">SALES IQ</div>
              <div className="text-[10px] text-zinc-500 font-mono mt-1 tracking-wide">STARTUP TIMES</div>
            </div>
          </div>
        </div>

        <div className="px-3 pb-2">
          <button
            data-testid="global-search-trigger"
            onClick={() => setSearchOpen(true)}
            className="w-full flex items-center gap-2 bg-zinc-900/80 hover:bg-zinc-800 rounded-lg border border-white/5 text-zinc-400 px-3 py-2 text-sm transition-colors"
          >
            <Search className="h-4 w-4" /> Search leads
            <kbd className="ml-auto text-[10px] font-mono border border-white/10 rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
        </div>

        <nav className="flex-1 py-2 space-y-0.5">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    isActive
                      ? "bg-zinc-800 text-white ring-1 ring-white/10 shadow-sm"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                  }`
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/5 flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-klein to-indigo-700 grid place-items-center text-xs font-bold text-white shrink-0">
            {(user?.name || "?").slice(0, 1)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">{user?.name}</div>
            <div className="text-[10px] text-zinc-500 font-mono uppercase">{ROLE_LABEL[user?.role]}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="text-zinc-500 hover:text-white transition-colors"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>

      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
      <LeadDrawer leadId={openId} open={!!openId} onClose={close} onChanged={bump} />
    </div>
  );
}
